import React, { useState } from 'react';
import Body from 'ui/Typography/Body';
import LoadVpc from 'ui/LoadVpc/LoadVpc';
import { useSelector } from 'react-redux';
import { showHamburgerMenu } from 'ducks/layout';
import { isLoadVpcSheetVisible, setLoadVpcSheetVisible } from 'ducks/sheets';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { DesignSourceEnum } from '@insights/insights-data-provider';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import OpenDesignCodeStateEnum from '@inter-ikea-kompis/component-open-design-code/lib/enums/OpenDesignCodeStateEnum';
import Title from 'ui/Typography/Title';
import SheetBox from 'ui/Sheet/SheetBox';
import { useAppDispatch } from 'ducks/hooks';
import Sheet from 'ui/Sheet/Sheet';
import SheetHeader from 'ui/Sheet/SheetHeader';
import { useSceneHandler } from 'contexts/sceneHandler';
import { AppRoutes } from 'routes';
import { startGame } from 'rangeConfig/eket/features/Ekris/EkrisFeature';
import { getRange } from 'utils/queryUtils';
import { isProduction } from 'utils/platform';

type LoadConfigurationEvent = {
  detail: {
    vpcCode: string;
  };
};

function LoadVPCSheet() {
  const history = useHistory();
  const [vpcLoadState, setVpcLoadState] = useState(OpenDesignCodeStateEnum.default);
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const visible = useSelector(isLoadVpcSheetVisible);
  const { loadVpcScene } = useSceneHandler();
  const enableEkris = !isProduction() && getRange() === 'eket';
  const onClose = () => {
    dispatch(setLoadVpcSheetVisible(false));
  };

  const onLoadConfiguration = async (e: LoadConfigurationEvent) => {
    const { vpcCode } = e.detail;
    if (enableEkris && vpcCode.toLowerCase() === 'ekris') {
      history.push(AppRoutes.PLANNER);
      dispatch(startGame());
      onClose();
      return;
    }
    setVpcLoadState(OpenDesignCodeStateEnum.loading);

    const scene = await loadVpcScene(vpcCode, DesignSourceEnum.enterCode).then(scene =>
      scene?.init()
    );

    if (scene) {
      dispatch(setLoadVpcSheetVisible(false));

      if (scene.initialized) {
        history.push(AppRoutes.PLANNER);
      } else if (scene.legacy) {
        history.push(AppRoutes.SUMMARY);
      }
    }

    setVpcLoadState(OpenDesignCodeStateEnum.default);
  };

  const goBackAndShowHamburgerMenu = () => {
    onClose();
    setTimeout(() => dispatch(showHamburgerMenu()), 10);
  };

  // Don't show back button on Gallery page
  const showBackButton = history.location.pathname !== '/';

  return (
    <Sheet onOverlayClick={onClose} visible={visible}>
      <SheetHeader
        backIcon={showBackButton}
        onCloseButtonClick={onClose}
        onBackButtonClick={goBackAndShowHamburgerMenu}
      />
      <SheetBox>
        <Title pb="s" type={ThemeFontStyleTypeEnum.headingMedium}>
          {formatMessage({ id: 'storageone.openDesignTitle' })}
        </Title>
        <Body lineHeight="tall" type={ThemeFontStyleTypeEnum.bodyMedium}>
          {formatMessage({ id: 'storageone.savedDesignCodeInfo' })}
        </Body>
        {visible && (
          <LoadVpc
            data-testid="kompis-load-vpc"
            mt="s"
            onOpen={onLoadConfiguration}
            state={vpcLoadState}
            data-state={vpcLoadState}
          />
        )}
      </SheetBox>
    </Sheet>
  );
}

export default LoadVPCSheet;
